import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
	getError,
	isResultsLimitExceededError,
	useAxiosInstance,
	usePoQuery,
	vehicleRegistryPreviewsQuery,
} from '@gov-nx/api';
import { useBusinessPersonLoader } from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { propEq } from '@gov-nx/core/types';
import { toStringDate, useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import { getVehicleGetters } from '../vehicleUtils';
import { FormDefinitionInstance, getDefaultValues } from './FormDefinitions';
import type {
	RegisterSVListContext,
	RegistrSVFormData,
	VehicleRegistryParams,
} from './context.types';
import { filterVehicles, sortVehiclesSubjektOd } from './filterUtils';
import { useFilters } from './useFilters';

const RegistrSVContext = createContext<Nullable<RegisterSVListContext>>(null);

interface RegistrZPContextProviderProps {
	code: PageCode;
	children: React.ReactNode;
}
const defaultValues = getDefaultValues();

export function RegistrSVSeznamContextProvider({
	code,
	children,
}: RegistrZPContextProviderProps) {
	const filterWindowOpened = useBoolean(false);
	const { filterParams, filter, setPartialFilter } = useFilters({
		code,
		defaultValues,
		onFilterRemove: (key, value) => {
			formDefinition.formMethods.setValue(key, value);
			if (key === 'filterStatus.provozovane' || key === 'filterStatus.zanik') {
				if (formDefinition.formMethods.getValues('filterStatus.vse')) {
					formDefinition.formMethods.setValue('filterStatus.vse', false);
				}
			}
			if (
				key === 'filterVztah.provozovatel' ||
				key === 'filterVztah.vlastnik'
			) {
				if (formDefinition.formMethods.getValues('filterVztah.vse')) {
					formDefinition.formMethods.setValue('filterVztah.vse', false);
				}
			}
		},
	});
	const formDefinition = FormDefinitionInstance({
		code,
		defaultValues,
	});
	const { axiosInstance } = useAxiosInstance();

	const ico = filter.typOsoby === 'PFO' ? filter.ico : undefined;
	const datumOd = filter.datumOd ? toStringDate(filter.datumOd) : undefined;
	const datumDo = filter.datumDo ? toStringDate(filter.datumDo) : undefined;

	const query = usePoQuery({
		queryKey: [
			'nahledyudaju',
			'zdrojUdaju',
			'REGISTR_VOZIDEL',
			ico,
			datumOd,
			datumDo,
		],
		refetchOnWindowFocus: false,
		queryFn: () =>
			vehicleRegistryPreviewsQuery(axiosInstance)<VehicleRegistryParams>({
				zdrojUdaju: 'REGISTR_VOZIDEL_V2',
				ico,
				start: 0,
				pocet: 100,
				datumOd,
				datumDo,
			}),
		retry: false,
	});

	const limitExceededError = query.error
		? isResultsLimitExceededError(query.error)
		: undefined;

	const { businessPerson, error: businessPersonError } =
		useBusinessPersonLoader();

	const dataBoxesPFO =
		businessPerson?.seznam?.filter(propEq('typ', 'PFO')) ?? [];
	const hasPFODataBox = dataBoxesPFO.length > 0;
	const typOsoby = formDefinition.formMethods.watch('typOsoby');

	useEffect(() => {
		if (dataBoxesPFO.length > 0 && typOsoby === 'PFO') {
			const { ico } = dataBoxesPFO[0];
			ico && formDefinition.formMethods.setValue('ico', `${ico}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [typOsoby, dataBoxesPFO]);

	useEffect(() => {
		formDefinition.formMethods.setValue('typOsoby', filter.typOsoby);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [filter.typOsoby]);

	const handleSubmit = (data: RegistrSVFormData) => {
		setPartialFilter(data);
		filterWindowOpened.setFalse();
	};

	const onReset = () => {
		setPartialFilter(defaultValues);
		formDefinition.formReset();
		filterWindowOpened.setFalse();
	};

	useEffect(() => {
		filter.datumOd &&
			formDefinition.formMethods.setValue('datumOd', filter.datumOd);
		filter.datumDo &&
			formDefinition.formMethods.setValue('datumDo', filter.datumDo);
		filter.filterText &&
			formDefinition.formMethods.setValue('filterText', filter.filterText);
		filter.filterStatus &&
			formDefinition.formMethods.setValue('filterStatus', filter.filterStatus);
		filter.filterVztah &&
			formDefinition.formMethods.setValue('filterVztah', filter.filterVztah);
		filter.filterVehicleStatus &&
			formDefinition.formMethods.setValue(
				'filterVehicleStatus',
				filter.filterVehicleStatus
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	useEffect(() => {
		const { setValue, watch, getValues } = formDefinition.formMethods;

		// HACK: calling setValue inside watch and useEffect makes setValue unusable
		const seValueTimeouted = (...args: Parameters<typeof setValue>) => {
			setTimeout(() => {
				setValue(...args);
			}, 0);
		};

		const subscription = watch((value, { name, type }) => {
			if (name === 'filterVehicleStatus') {
				setPartialFilter({
					filterVehicleStatus: getValues('filterVehicleStatus'),
					page: defaultValues.page,
				});
			}

			if (name?.startsWith('filterStatus.')) {
				const provozovane = value['filterStatus']?.provozovane ?? false;
				const vse = value['filterStatus']?.vse ?? false;
				const zanik = value['filterStatus']?.zanik ?? false;

				if (name === 'filterStatus.vse' && type === 'change') {
					if (provozovane !== vse)
						seValueTimeouted('filterStatus.provozovane', vse);
					if (zanik !== vse) seValueTimeouted('filterStatus.zanik', vse);
				}

				if (name !== 'filterStatus.vse' && type === 'change') {
					const allChecked = zanik && provozovane;
					if (allChecked && !vse) seValueTimeouted('filterStatus.vse', true);
					if (!allChecked && vse) seValueTimeouted('filterStatus.vse', false);
				}
			}

			if (name?.startsWith('filterVztah.')) {
				const provozovatel = value['filterVztah']?.provozovatel ?? false;
				const vse = value['filterVztah']?.vse ?? false;
				const vlastnik = value['filterVztah']?.vlastnik ?? false;

				if (name === 'filterVztah.vse' && type === 'change') {
					if (provozovatel !== vse)
						seValueTimeouted('filterVztah.provozovatel', vse);
					if (vlastnik !== vse) seValueTimeouted('filterVztah.vlastnik', vse);
				}

				if (name !== 'filterVztah.vse' && type === 'change') {
					const allChecked = vlastnik && provozovatel;
					if (allChecked && !vse) seValueTimeouted('filterVztah.vse', true);
					if (!allChecked && vse) seValueTimeouted('filterVztah.vse', false);
				}
			}
		});

		return () => subscription.unsubscribe();
	}, [formDefinition.formMethods, filterWindowOpened.value, setPartialFilter]);

	const vehicles = useMemo(
		() =>
			query.data?.filter(filterVehicles(filter)).sort(sortVehiclesSubjektOd),
		[query.data, filter]
	);

	const totalFilteredVehicles = vehicles?.length ?? 0;
	const vehiclesPaginated =
		vehicles?.slice(
			(filter.page - 1) * filter.pageSize,
			filter.page * filter.pageSize
		) ?? [];

	const showNoVehiclesBanner = vehicles?.length === 0 && !limitExceededError;

	return (
		<RegistrSVContext.Provider
			value={{
				code,
				controls: {
					resultsLimitExceeded: limitExceededError?.extPopis,
					initialError:
						getError(query.error, { ignoreStatus: 400 }) || businessPersonError,
					initialLoading: query.isLoading,
					processLoading: query.isRefetching,
				},
				totalFilteredVehicles,
				vehiclesPaginated,
				showNoVehiclesBanner,
				formDefinition,
				vehicleGetters: getVehicleGetters,
				openFilterModal: filterWindowOpened.setTrue,
				isFilterWindowOpen: filterWindowOpened.value,
				closeFilterModal: filterWindowOpened.setFalse,
				onSubmit: formDefinition.formMethods.handleSubmit(handleSubmit),
				onReset,
				filterParams,
				hasPFODataBox,
				typOsoby,
				ico,
				dataBoxesPFO,
				filter,
				setPartialFilter,
			}}>
			{children}
		</RegistrSVContext.Provider>
	);
}

export const useRegistrSVSeznamContext = (): RegisterSVListContext =>
	useContext(RegistrSVContext) as RegisterSVListContext;
